import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, Trash2, FileCheck2, Ban, Printer } from "lucide-react";

const RO_STATUS = {
  "Pending": "text-yellow-500 bg-yellow-500/10 border-yellow-500/30",
  "Converted to PO": "text-emerald-500 bg-emerald-500/10 border-emerald-500/30",
  "Revoked": "text-muted-foreground bg-secondary border-border",
};
const RoStatus = ({ s }) => <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border ${RO_STATUS[s] || RO_STATUS.Pending}`}>{s}</span>;

export function ReorderRecords({ refreshSignal = 0, className = "" }) {
  const [records, setRecords] = useState([]);
  const [editRec, setEditRec] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [confirm, setConfirm] = useState(null); // { type: "delete" | "revoke", rec }

  const loadRecords = async () => {
    try { const r = await api.get("/reorders"); setRecords(r.data); } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadRecords(); }, [refreshSignal]); // eslint-disable-line

  const openEditRec = (r) => { setEditRec(r); setEditLines((r.lines || []).map(l => ({ ...l }))); };

  const saveEditRec = async () => {
    try {
      await api.patch(`/reorders/${editRec.id}`, {
        lines: editLines.map(l => ({ item_id: l.item_id, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0, supplier_id: editRec.supplier_id })),
      });
      toast.success(`${editRec.reorder_number} updated & resubmitted as Pending`);
      setEditRec(null);
      loadRecords();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const convertRec = async (r) => {
    try {
      const { data } = await api.post(`/reorders/${r.id}/convert-po`);
      toast.success(`${r.reorder_number} converted to PO ${data.po?.txn_number || ""}`);
      loadRecords();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doConfirm = async () => {
    try {
      if (confirm.type === "delete") { await api.delete(`/reorders/${confirm.rec.id}`); toast.success(`${confirm.rec.reorder_number} deleted`); }
      else { await api.post(`/reorders/${confirm.rec.id}/revoke`); toast.success(`${confirm.rec.reorder_number} revoked — now editable for correction`); }
      setConfirm(null);
      loadRecords();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const printReorder = async (r) => {
    try {
      const res = await api.get(`/reorders/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className={`border border-border bg-card rounded-sm overflow-x-auto ${className}`}>
      <div className="p-3 border-b border-border flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Reorder Records</div>
        <div className="text-xs text-muted-foreground font-mono" data-testid="records-count">{records.length} record{records.length === 1 ? "" : "s"}</div>
      </div>
      <table className="w-full text-sm" data-testid="reorder-records-table">
        <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <tr>
            <th className="text-left p-3">Reorder Number</th><th className="text-left p-3">Date</th>
            <th className="text-left p-3">Supplier</th><th className="text-left p-3">Company</th>
            <th className="text-right p-3">Total Items</th><th className="text-right p-3">Total Reorder Qty</th>
            <th className="text-right p-3">Total Amount</th><th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground" data-testid="records-empty">No reorder records yet — create one from a Reorder List</td></tr>}
          {records.map(r => (
            <tr key={r.id} data-testid={`ro-row-${r.id}`} className="border-t border-border hover:bg-secondary/50">
              <td className="p-3 font-mono text-primary" data-testid={`ro-number-${r.id}`}>{r.reorder_number}</td>
              <td className="p-3 text-xs">{(r.created_at || "").slice(0, 10)}</td>
              <td className="p-3">{r.supplier_name}</td>
              <td className="p-3 text-xs">{(r.companies || []).join(", ") || "-"}</td>
              <td className="p-3 text-right font-mono">{r.total_items}</td>
              <td className="p-3 text-right font-mono">{r.total_qty}</td>
              <td className="p-3 text-right font-mono">{inr(r.amount)}</td>
              <td className="p-3" data-testid={`ro-status-${r.id}`}><RoStatus s={r.status}/></td>
              <td className="p-3 text-right whitespace-nowrap">
                {(r.status === "Pending" || r.status === "Revoked") && <>
                  <Button data-testid={`ro-edit-${r.id}`} size="sm" variant="ghost" title="Edit" onClick={() => openEditRec(r)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`ro-delete-${r.id}`} size="sm" variant="ghost" title="Delete" onClick={() => setConfirm({ type: "delete", rec: r })} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </>}
                {r.status === "Pending" && <>
                  <Button data-testid={`ro-convert-${r.id}`} size="sm" variant="ghost" title="Convert to PO" onClick={() => convertRec(r)} className="h-7 w-7 p-0 text-emerald-500"><FileCheck2 className="w-3 h-3"/></Button>
                  <Button data-testid={`ro-revoke-${r.id}`} size="sm" variant="ghost" title="Revoke" onClick={() => setConfirm({ type: "revoke", rec: r })} className="h-7 w-7 p-0 text-yellow-500"><Ban className="w-3 h-3"/></Button>
                </>}
                <Button data-testid={`ro-print-${r.id}`} size="sm" variant="ghost" title="Print / Download PDF" onClick={() => printReorder(r)} className="h-7 w-7 p-0"><Printer className="w-3 h-3"/></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!editRec} onOpenChange={(v) => !v && setEditRec(null)}>
        <DialogContent className="rounded-sm bg-card border-border max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ro-edit-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">Edit {editRec?.reorder_number} · {editRec?.supplier_name}</DialogTitle></DialogHeader>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr><th className="text-left p-2">Item</th><th className="text-left p-2">Part No</th><th className="text-right p-2 w-24">Qty</th><th className="text-right p-2 w-28">Rate</th><th className="text-right p-2 w-24">Total</th></tr>
            </thead>
            <tbody>
              {editLines.map((l, i) => (
                <tr key={l.item_id} className="border-t border-border">
                  <td className="p-2">{l.item_name}</td>
                  <td className="p-2 font-mono text-xs text-muted-foreground">{l.part_number || "-"}</td>
                  <td className="p-2"><Input data-testid={`ro-edit-qty-${l.item_id}`} type="number" min="0" value={l.qty} onChange={(e) => setEditLines(ls => ls.map((x, xi) => xi === i ? { ...x, qty: e.target.value } : x))} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                  <td className="p-2"><Input data-testid={`ro-edit-rate-${l.item_id}`} type="number" min="0" value={l.rate} onChange={(e) => setEditLines(ls => ls.map((x, xi) => xi === i ? { ...x, rate: e.target.value } : x))} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                  <td className="p-2 text-right font-mono text-xs">{inr((Number(l.qty) || 0) * (Number(l.rate) || 0) * (1 + ((l.cgst || 0) + (l.sgst || 0)) / 100))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setEditRec(null)} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="ro-edit-save" onClick={saveEditRec} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">Save & Resubmit</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="rounded-sm bg-card border-border max-w-sm" data-testid="ro-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase">{confirm?.type === "delete" ? "Delete" : "Revoke"} {confirm?.rec?.reorder_number}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{confirm?.type === "delete" ? "This permanently deletes the reorder record." : "The reorder will be cancelled and marked Revoked. It stays editable for correction and resubmission."}</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setConfirm(null)} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="ro-confirm-yes" onClick={doConfirm} className={`rounded-sm text-xs uppercase tracking-wider ${confirm?.type === "delete" ? "bg-destructive text-destructive-foreground" : "bg-yellow-600 text-white"}`}>{confirm?.type === "delete" ? "Delete" : "Revoke"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
